import React, { useState, useEffect, useMemo } from 'react';
import { IcSparkles, IcChevronLeft, IcChevronRight, IcSearch, IcUser, IcBanknote, IcCheckCircle, IcAlertCircle, IcFilters, IcClose } from '../../theme/icons';
import salesService from '../../services/salesService';
import VenteTabs from '../../components/VenteTabs';
import { CardSkeleton } from '../../components/Skeleton';
import { matchesSearch } from '../../utils/search';

// « Historique service » — soins / services vendus. Paginates BY DAY like the sales
// history, but flattens each sale into its SERVICE lines only.
const DAY_PAGE_SIZE = 1000;

const fmtDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmt = (a) => `${parseFloat(a || 0).toFixed(3)} DT`;

// Status filter chips — same set as the sales history panel.
const STATUS_TABS = [
  { value: '', label: 'Toutes', icon: null },
  { value: 'paid', label: 'Payées', icon: IcCheckCircle },
  { value: 'credit', label: 'Crédit', icon: IcAlertCircle },
];

const ServiceHistory = () => {
  const [currentDay, setCurrentDay] = useState(fmtDateInput(new Date()));
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [filters, setFilters] = useState({ clientName: '', serviceName: '' });

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await salesService.getSalesPage({
          startDate: currentDay, endDate: currentDay, page: 1, pageSize: DAY_PAGE_SIZE,
        });
        if (active) setSales(res.results || []);
      } catch (err) {
        if (active) { console.error('Error fetching service history:', err); setError('Erreur lors du chargement.'); setSales([]); }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [currentDay]);

  // Flatten every sale into one row per SERVICE line.
  const rows = useMemo(() => {
    const out = [];
    sales.forEach((sale) => {
      const client = sale.customer
        ? `${sale.customer.first_name || ''} ${sale.customer.last_name || ''}`.trim() || sale.customer_name
        : (sale.customer_name || 'Client de passage');
      (sale.service_details || []).forEach((s, i) => {
        const price = parseFloat(s.price) || 0;
        const remise = parseFloat(s.remise) || 0;
        const fp = remise > 0 ? price * (1 - remise / 100) : price;
        const total = s.total_detail != null ? Number(s.total_detail) : fp * (s.quantity || 1);
        out.push({
          key: `${sale.id}-s${i}`,
          saleId: sale.id,
          name: s.service_name || s.service?.name || 'Service',
          quantity: s.quantity || 1,
          total,
          client,
          paid: Number(sale.credit || 0) <= 0,
          credit: Number(sale.credit || 0),
          date: sale.date_added,
        });
      });
    });
    let list = out;
    // Règle Ki Para (utils/search.js) : début de mot, sans accents.
    list = list.filter((r) => matchesSearch(search, [r.name, r.client]));
    if (statusFilter === 'paid') list = list.filter((r) => r.paid);
    if (statusFilter === 'credit') list = list.filter((r) => !r.paid);
    list = list.filter((r) => matchesSearch(filters.clientName, r.client));
    list = list.filter((r) => matchesSearch(filters.serviceName, r.name));
    return list;
  }, [sales, search, statusFilter, filters]);

  const hasActiveFilters = filters.clientName || filters.serviceName || statusFilter;
  const panelFilterCount =
    (filters.clientName ? 1 : 0) + (filters.serviceName ? 1 : 0) + (statusFilter ? 1 : 0);
  const clearFilters = () => { setFilters({ clientName: '', serviceName: '' }); setStatusFilter(''); };

  const totals = useMemo(() => rows.reduce(
    (acc, r) => ({ count: acc.count + 1, total: acc.total + (r.total || 0) }),
    { count: 0, total: 0 },
  ), [rows]);

  const isToday = currentDay === fmtDateInput(new Date());
  const shiftDay = (delta) => {
    const d = new Date(currentDay + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setCurrentDay(fmtDateInput(d));
  };
  const dayLabel = (() => {
    try { return new Date(currentDay + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }); }
    catch (e) { return currentDay; }
  })();

  return (
    <div className="w-full px-4 pt-2 pb-4">
      <VenteTabs />

      {/* Toolbar row under the page tabs — Filtres button like on the
          historique-ventes page (owner Studio); this row occupies the
          `tabs-gap` space, keeping both history pages aligned. */}
      <div className="flex gap-3 mb-3 justify-end items-center">
        {/* Composants-2 §3 : hors formulaire, Réinitialiser = lien rouge */}
        {hasActiveFilters && (
          <button type="button" onClick={clearFilters} className="link-reset gap-1.5 px-3 py-1.5 text-sm">
            Réinitialiser
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowFilterPanel((v) => !v)}
          className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
        >
          <IcFilters className="text-sm" /> Filtres
          {panelFilterCount > 0 && (
            <span className="bdg-count static ml-2">{panelFilterCount}</span>
          )}
        </button>
      </div>

      {/* Filter panel — carte de filtres Brand Kit §05 (rayon 6, padding 12, gap 9). */}
      {showFilterPanel && (
        <div className="flt-card rounded-2xl p-4 mb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_TABS.map((s) => {
              const active = statusFilter === s.value;
              return (
                <button
                  key={s.value || 'all'} onClick={() => setStatusFilter(s.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition border ${
                    active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {s.icon && <s.icon className="text-xs" />} {s.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="flt-label" htmlFor="svh-filter-client">Client</label>
              <div className="relative">
                <IcUser size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  id="svh-filter-client"
                  type="text" value={filters.clientName}
                  onChange={(e) => setFilters((prev) => ({ ...prev, clientName: e.target.value }))}
                  placeholder="Rechercher un client (nom)…"
                  className="inp w-full pl-9 pr-3 py-2.5 rounded-xl text-gray-900"
                />
              </div>
            </div>
            <div>
              <label className="flt-label" htmlFor="svh-filter-service">Service</label>
              <div className="relative">
                <IcSparkles size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  id="svh-filter-service"
                  type="text" value={filters.serviceName}
                  onChange={(e) => setFilters((prev) => ({ ...prev, serviceName: e.target.value }))}
                  placeholder="Rechercher un service…"
                  className="inp w-full pl-9 pr-3 py-2.5 rounded-xl text-gray-900"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Day navigation — Composants-2 §4 : pastilles 40px 13px 600,
          chevrons 15px, titre 15px 600 centré (flex-1). */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <button type="button" onClick={() => shiftDay(-1)} className="btn-day gap-1 px-3 py-2 text-sm"><IcChevronLeft /> Jour précédent</button>
        <span className="text-sm font-semibold text-gray-900 capitalize text-center flex-1">{dayLabel}</span>
        <button type="button" onClick={() => shiftDay(1)} disabled={isToday} className="btn-day gap-1 px-3 py-2 text-sm">Jour suivant <IcChevronRight /></button>
      </div>

      {/* KPI + search */}
      {/* Grille 3 colonnes (et non flex) : en flex, le padding des cases comptait dans
          la base et le champ de recherche sortait 26px plus étroit — la grille donne
          la même largeur aux 3 cellules et aux 3 cases vides de la rangée suivante. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {/* Owner 2026-09-10 : mêmes cases que la barre KPI de /seller-history —
            kit .kpi, icône NEUTRE 11px en haut à droite (.kpi-icon) à la place
            de la pastille 40px teintée, libellé en capitales, valeur tabulaire.
            min-h 91px = hauteur mesurée des cases de /seller-history (qui portent
            une sous-ligne .kpi-sub) — owner 2026-09-10, taille seule. */}
        <div className="kpi min-w-0 min-h-[91px] rounded-2xl p-3">
          <IcSparkles size={11} className="kpi-icon w-[11px] h-[11px]" />
          <div className="kpi-label uppercase pr-6 text-xs mb-1 truncate">Services vendus</div>
          <div className="kpi-value text-base sm:text-lg truncate">{totals.count}</div>
        </div>
        <div className="kpi min-w-0 min-h-[91px] rounded-2xl p-3">
          <IcBanknote size={11} className="kpi-icon w-[11px] h-[11px]" />
          <div className="kpi-label uppercase pr-6 text-xs mb-1 truncate">Total</div>
          <div className="kpi-value text-base sm:text-lg truncate">{fmt(totals.total)}</div>
        </div>
        <div className="min-w-0 relative">
          {/* Composants-2 §2 : recherche 44px, icône search 16px #6B7280 */}
          <IcSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher service / client..." className="inp w-full h-full min-h-[52px] pl-10 pr-3 py-2 rounded-2xl" />
        </div>
      </div>

      {/* Owner 2026-09-10 : 3 cases KPI VIDES réservées pour de futurs indicateurs —
          même kit .kpi, même hauteur (91px) et même largeur (3 par rangée, gap-3)
          que « Services vendus ». À remplir plus tard ; ne rien afficher d'ici là. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="kpi min-w-0 min-h-[91px] rounded-2xl p-3" aria-hidden="true" />
        ))}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>}

      {loading ? (
        <div className="text-center py-16"><CardSkeleton /></div>
      ) : rows.length === 0 ? (
        /* État vide (Brand Kit §05) : cadre pointillé, icône 22px, phrase 13px 600 */
        <div className="empty-ki rounded-2xl p-12">
          <IcSparkles size={36} className="empty-ki-icon w-9 h-9 mx-auto mb-3" />
          <p className="empty-ki-title text-base">Aucun service vendu ce jour.</p>
          <p className="empty-ki-sub mb-0">Essayez un autre jour ou retirez les filtres.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-3 px-4 py-3">
              <span className="h-10 w-10 shrink-0 rounded-xl bg-green-50 text-green-700 flex items-center justify-center"><IcSparkles /></span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-800 truncate">{r.name}{r.quantity > 1 && <span className="text-gray-400 font-normal"> × {r.quantity}</span>}</p>
                <p className="text-xs text-gray-500 inline-flex items-center gap-1"><IcUser size={10} /> {r.client}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold text-gray-900">{fmt(r.total)}</p>
                <span className={`bdg ${r.paid ? 'bdg-pos' : 'bdg-critical'}`}>
                  {r.paid ? <IcCheckCircle size={10} /> : <IcAlertCircle size={10} />} {r.paid ? 'Payé' : `Non payé · Crédit ${fmt(r.credit)}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceHistory;
